import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  merge,
  Subscription,
} from 'rxjs';
import { LocalStorageServiceBase } from 'src/app/core/services/localStorage-serviceBase';
import { LocationEntity } from 'src/app/core/services/location/domain/entities/location.entity';
import { ProvinceEntity } from 'src/app/core/services/location/domain/entities/province.entity';
import { GetDepartmentsUseCase } from 'src/app/core/services/location/domain/usecases/get-departments.usecase';
import { GetLocationsUseCase } from 'src/app/core/services/location/domain/usecases/get-locations.usecase';
import { GetProvinceUseCase } from 'src/app/core/services/location/domain/usecases/get-province.usecase';
import AESEncryptDecryptService from 'src/app/core/utils/crypto';
import { LocalitiesEntity } from 'src/app/features/claims/data/models/location.model';
import { ClaimCollectiveEntity } from 'src/app/features/claims/domain/entities/claim-colective.entity';
import { GetCompanyCuilUseCase } from 'src/app/features/company/domain/usecases/get-company-colective.usecase';
import { UserData } from 'src/app/features/user/domain/entities/user-data.entity';
import { Departament } from 'src/app/features/user/presentation/components/New-User-SuperAdmin/newUser.component';

interface Person {
  key: string;
  name: string;
}
@Component({
  selector: 'app-claimant-details-colective',
  templateUrl: './claimant-details-colective.component.html',
  styleUrls: ['./claimant-details-colective.component.css'],
})
export class ClaimantDetailsColectiveComponent {
  @Output() claimantDetailsForm = new EventEmitter<FormGroup>();
  @Output() stepIndex: EventEmitter<number> = new EventEmitter();
  @Output() activeModal: EventEmitter<boolean> = new EventEmitter();
  @Input() claim?: ClaimCollectiveEntity;
  @Input() currentStepIndex: number = 0;
  @Input() userData?: UserData;
  @Output() cancelAction = new EventEmitter<boolean>();

  formGroup!: FormGroup;

  provinces: ProvinceEntity[] = [];
  localities: LocalitiesEntity[] = [];
  departments: Departament[] = [];
  isEdit: boolean = false;

  isRepresentative: boolean = false;
  private AES = new AESEncryptDecryptService();

  formChangeSubscription?: Subscription;
  companyFound: boolean = true;

  typeOfPerson: Person[] = [
    { name: 'Persona física', key: 'physicalPerson' },
    { name: 'Persona jurídica', key: 'legalPerson' },
  ];

  constructor(
    private getProvince: GetProvinceUseCase,
    private getDepartmentsUsecase: GetDepartmentsUseCase,
    private getLocations: GetLocationsUseCase,
    private formBuilder: FormBuilder,
    private localStorageService: LocalStorageServiceBase,
    private getCompanyUseCase: GetCompanyCuilUseCase,
    private router: Router
  ) {
    this.isRepresentative =
      this.AES.decrypt(this.localStorageService.loadStorageData('user_rol')) ==
      'Representante gremial';
    this.isEdit = this.router.url.includes('update');
  }

  async ngOnInit(): Promise<void> {
    this.initForms();
    await this.initializeComponent();

    this.formChangeSubscription = merge(
      this.formGroup.valueChanges,
      this.formGroup.statusChanges
    )
      .pipe(filter(() => this.currentStepIndex === 1))
      .subscribe(() => {
        this.claimantDetailsForm.emit(this.formGroup);
      });
    this.formGroup
      .get('provinceCompany')
      ?.valueChanges.subscribe((selectedProvince: any) => {
        if (selectedProvince) {
          this.loadLocalities(selectedProvince);
          this.formGroup.patchValue(
            { provincia: selectedProvince },
            { emitEvent: false }
          );
        }
      });
    this.setupCuitSearchListener();
  }

  setupCuitSearchListener() {
    if (this.isRepresentative && this.currentStepIndex === 1) {
      this.formGroup
        .get('cuitCompany')
        ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(async (value) => {
          if (this.isCuitInputComplete(value)) {
            await this.searchCompanyByCuit(value);
          }
        });
    }
  }

  private isCuitInputComplete(value: unknown): boolean {
    const digits = String(value ?? '').replace(/\D/g, '');
    return digits.length === 11;
  }

  private shouldResetAfterCompanyLookupFailure(): boolean {
    return !this.isRepresentative || this.currentStepIndex === 1;
  }

  async searchCompanyByCuit(cuit: any) {
    if (!this.isCuitInputComplete(cuit)) {
      return;
    }
    try {
      const cuitLimpio = String(cuit ?? '').replace(/\D/g, '');
      const result = await this.getCompanyUseCase.execute(cuitLimpio);
      if (result && 'razon_social' in result) {
        this.companyFound = true;
        this.fillFormWithCompanyData(result);
      } else {
        this.companyFound = false;
        if (this.shouldResetAfterCompanyLookupFailure()) {
          this.resetAfterCompanyNotFound(cuit);
        }
      }
    } catch (error) {
      console.error('Error al buscar empresa por CUIT:', error);
      this.companyFound = false;
      if (this.shouldResetAfterCompanyLookupFailure()) {
        this.resetAfterCompanyNotFound(cuit);
      }
    }
  }

  private resetAfterCompanyNotFound(cuitMasked: unknown): void {
    const cuitActual = cuitMasked;
    this.localities = [];

    const claimantTypePerformance = !this.isRepresentative
      ? this.userData?.tipo_actuacion ?? ''
      : this.userData?.type_responsibility ?? '';

    this.formGroup.patchValue(
      {
        razonSocial: '',
        companyEmail: '',
        companyPhone: '',
        streetCompany: '',
        numberCompany: '',
        floorApartmentCompany: '',
        towerCompany: '',
        postalCodeCompany: '',
        provinceCompany: '',
        cityCompany: '',
        claimantTypePerformance,
      },
      { emitEvent: false },
    );

    this.formGroup
      .get('cuitCompany')
      ?.setValue(cuitActual, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });

    if (this.currentStepIndex === 1) {
      this.claimantDetailsForm.emit(this.formGroup);
    }
  }

  fillFormWithCompanyData(companyData: any) {
    this.formGroup.patchValue({
      razonSocial: companyData.razon_social || '',
      companyEmail: companyData.email || '',
      companyPhone: companyData.phone || '',
      streetCompany: companyData.street || '',
      numberCompany: companyData.number || '',
      floorApartmentCompany: companyData.floor_apartment || '',
      towerCompany: companyData.tower || '',
      postalCodeCompany: companyData.postal_code || '',
      provinceCompany: companyData.province || '',
      cityCompany: companyData.city || '',
      claimantTypePerformance: !this.isRepresentative
        ? this.userData?.tipo_actuacion || ''
        : companyData.type_responsibility || '',
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    await this.initializeComponent();
    if (changes['claim'] && changes['claim'].currentValue) {
      await this.initForms();
      await this.initializeFormWithClaimData();
    }
    if (changes['userData'] && changes['userData'].currentValue) {
      await this.loadUserData();
    }
  }

  private initForms(): void {
    this.formGroup = this.formBuilder.group({
      razonSocial: ['', Validators.required],
      cuitCompany: ['', Validators.required],
      companyEmail: ['', [Validators.required, Validators.email]],
      companyPhone: [
        '',
        [
          Validators.minLength(7),
          Validators.maxLength(15),
        ],
      ],
      streetCompany: ['', Validators.required],
      numberCompany: ['', Validators.required],
      floorApartmentCompany: [''],
      towerCompany: [''],
      postalCodeCompany: [
        '',
        [
          Validators.required,
          Validators.maxLength(10),
          Validators.minLength(4),
        ],
      ],
      provinceCompany: ['', Validators.required],
      cityCompany: ['', Validators.required],
      claimantTypePerformance: ['', Validators.required],
      departmentId: ['', Validators.required],
      type_of_person: ['', Validators.required],
    });
    if (!this.isRepresentative) {
      this.formGroup.get('type_of_person')?.clearValidators();
      this.formGroup.get('type_of_person')?.updateValueAndValidity();
    }
    if (this.isRepresentative && this.currentStepIndex === 0) {
      this.formGroup.addControl(
        'representative_last_name',
        this.formBuilder.control('', Validators.required)
      );
      this.formGroup.addControl(
        'representative_name',
        this.formBuilder.control('', Validators.required)
      );
      this.formGroup.get('type_of_person')?.clearValidators();
      this.formGroup.get('type_of_person')?.updateValueAndValidity();
      this.formGroup.get('razonSocial')?.clearValidators();
      this.formGroup.get('razonSocial')?.updateValueAndValidity();
    }
    if (this.isRepresentative && this.currentStepIndex === 1) {
      this.formGroup.get('type_of_person')?.updateValueAndValidity();

      this.formGroup.get('departmentId')?.clearValidators();
      this.formGroup.get('departmentId')?.updateValueAndValidity();

      this.formGroup.get('claimantTypePerformance')?.clearValidators();
      this.formGroup.get('claimantTypePerformance')?.updateValueAndValidity();

      this.formGroup.get('representative_name')?.clearValidators();
      this.formGroup.get('representative_name')?.updateValueAndValidity();

      this.formGroup.get('representative_last_name')?.clearValidators();
      this.formGroup.get('representative_last_name')?.updateValueAndValidity();
    }
  }

  async loadUserData() {
    if (this.userData && !this.isEdit) {
      if (this.isRepresentative) {
        this.formGroup.patchValue({
          representative_name: this.userData.name,
          representative_last_name: this.userData.lastName,
          cuitCompany: this.userData.cuil,
          companyEmail: this.userData.email,
          companyPhone: this.userData.phone,
          streetCompany: this.userData.street,
          numberCompany: this.userData.number,
          floorApartmentCompany: this.userData.floorApartment,
          towerCompany: this.userData.tower,
          postalCodeCompany: this.userData.postalCode,
          provinceCompany: this.getProvinceIdByName(this.userData.province),
          cityCompany: this.userData.city,
          claimantTypePerformance: this.userData.type_responsibility,
          claimantPosition: this.userData.cargo_empresa,
          birthday: this.parseDateBirth(this.userData.birthDate || ''),
        });
      } else {
        this.searchCompanyByCuit(this.userData.company_cuit);
        this.formGroup.patchValue({
          claimantTypePerformance: this.userData.tipo_actuacion,
          cuitCompany: this.userData.company_cuit,
        });
      }
    }
  }

  private getProvinceIdByName(provinceName: string): string | undefined {
    const province = this.provinces.find((p) => p.id === provinceName);
    return province ? province.name : undefined;
  }

  async initializeComponent(): Promise<void> {
    await this.loadProvinces();
    this.loadDepartments();
  }

  async initializeFormWithClaimData(): Promise<void> {
    if (this.isRepresentative && this.currentStepIndex === 1) {
      this.formGroup.patchValue({
        razonSocial: this.claim?.razonSocial || '',
        cuitCompany: this.claim?.cuitCompany || '',
        companyEmail: this.claim?.companyEmail || '',
        companyPhone: this.claim?.companyPhone || '',
        streetCompany: this.claim?.streetCompany || '',
        numberCompany: this.claim?.numberCompany || '',
        floorApartmentCompany: this.claim?.floorApartmentCompany || '',
        towerCompany: this.claim?.towerCompany || '',
        postalCodeCompany: this.claim?.postalCodeCompany || '',
        provinceCompany: this.claim?.provinceCompany || '',
        cityCompany: this.claim?.cityCompany || '',
        type_of_person: this.claim?.type_of_person,
      });
      this.claimantDetailsForm.emit(this.formGroup);
    } else {
      const isRepresentative =
        this.isRepresentative && this.currentStepIndex === 0;
      this.formGroup.patchValue({
        razonSocial: this.claim?.razonSocial,
        representative_last_name: isRepresentative
          ? this.claim?.representativeLastName
          : '',
        representative_name: isRepresentative
          ? this.claim?.representativeName
          : '',
        cuitCompany:
          (isRepresentative
            ? this.claim?.representativeCuil
            : this.claim?.cuitCompany) || '',
        companyEmail:
          (isRepresentative
            ? this.claim?.representativeEmail
            : this.claim?.companyEmail) || '',
        companyPhone:
          (isRepresentative
            ? this.claim?.representativePhone
            : this.claim?.companyPhone) || '',
        streetCompany: isRepresentative
          ? this.claim?.representativeStreet
          : this.claim?.streetCompany,
        numberCompany:
          (isRepresentative
            ? this.claim?.representativeNumber
            : this.claim?.numberCompany) || '',
        floorApartmentCompany:
          (isRepresentative
            ? this.claim?.representativeFloorApartment
            : this.claim?.floorApartmentCompany) || '',
        towerCompany:
          (isRepresentative
            ? this.claim?.representativeTower
            : this.claim?.towerCompany) || '',
        postalCodeCompany:
          (isRepresentative
            ? this.claim?.representativePostalCode
            : this.claim?.postalCodeCompany) || '',
        provinceCompany:
          (isRepresentative
            ? this.claim?.representativeProvince
            : this.claim?.provinceCompany) || '',
        cityCompany:
          (isRepresentative
            ? this.claim?.representativeCity
            : this.claim?.cityCompany) || '',
        departmentId: this.claim?.departmentId || '',
        claimantTypePerformance:
          (isRepresentative
            ? this.claim?.type_of_person
            : this.claim?.claimantTypePerformance) || '',
      });
    }
  }

  async loadProvinces() {
    try {
      const result = await this.getProvince.execute();
      if (Array.isArray(result)) {
        this.provinces = result;
      }
    } catch (error) {
      console.error('Error fetching provinces', error);
    }
  }

  async loadLocalities(provinceId: string) {
    try {
      const localities = await this.getLocations.execute(provinceId);
      if (Array.isArray(localities)) {
        const localitiesList = this.putIdToLocalitiesList(localities);

        this.localities = localitiesList;
      }
    } catch (error) {
      console.error('Error fetching localities', error);
    }
  }

  putIdToLocalitiesList(list: LocationEntity[]): LocalitiesEntity[] {
    let listWithId: LocalitiesEntity[] = [];
    for (let i = 0; i < list.length; i++) {
      let city: LocalitiesEntity = {
        id: i,
        name: list[i].name,
      };
      listWithId.push(city);
    }
    return listWithId;
  }

  async loadDepartments() {
    const departments = await this.getDepartmentsUsecase.execute();
    if (Array.isArray(departments)) {
      const transformedDepartments = this.putIdToDepartmentsList(departments);
      this.departments = [
        { id: 'Otro', name: 'Otro' },
        ...transformedDepartments,
      ];
    }
  }

  putIdToDepartmentsList(list: Departament[]): Departament[] {
    let listWithId: Departament[] = [];
    for (let i = 0; i < list.length; i++) {
      let department: Departament = {
        id: list[i].id,
        name: list[i].name,
      };
      listWithId.push(department);
    }
    return listWithId;
  }

  hasErrors(controlName: string, errorType: string) {
    return (
      this.formGroup.get(controlName)?.hasError(errorType) &&
      this.formGroup.get(controlName)?.touched
    );
  }

  nextStep() {
    const formValue = this.formGroup.value;
    const isRepresentative =
      this.isRepresentative && this.currentStepIndex === 0;

    const mappedData = {
      ...(isRepresentative
        ? {
            representative_last_name: formValue.representative_last_name,
            representative_name: formValue.representative_name,
            representative_cuil: formValue.cuitCompany,
            representative_email: formValue.companyEmail,
            representative_phone: formValue.companyPhone,
            representative_street: formValue.streetCompany,
            representative_number: formValue.numberCompany.toString(),
            representative_floor_apartment: formValue.floorApartmentCompany,
            representative_tower: formValue.tower,
            representative_postal_code: formValue.postalCodeCompany.toString(),
            representative_province: formValue.provinceCompany,
            representative_city: formValue.cityCompany,
            claimantTypePerformance: formValue.claimantTypePerformance,
            departmentId: formValue.departmentId,
          }
        : {
            razonSocial: formValue.razonSocial,
            cuitCompany: formValue.cuitCompany,
            companyEmail: formValue.companyEmail,
            companyPhone: formValue.companyPhone,
            streetCompany: formValue.streetCompany,
            numberCompany: formValue.numberCompany.toString(),
            floorApartmentCompany: formValue.floorApartmentCompany,
            towerCompany: formValue.towerCompany,
            postalCodeCompany: formValue.postalCodeCompany.toString(),
            provinceCompany: formValue.provinceCompany,
            cityCompany: formValue.cityCompany,
            departmentId: formValue.departmentId,
            claimantTypePerformance: formValue.claimantTypePerformance,
          }),
    };
    this.claimantDetailsForm.emit(this.formBuilder.group(mappedData));
    this.stepIndex.emit(1);
  }

  parseDateBirth(date: any): Date | null {
    if (!date) return null;
    return new Date(date);
  }

  cancel() {
    this.cancelAction.emit(this.isRepresentative);
    this.claimantDetailsForm.emit(this.formGroup);
    this.activeModal.emit(true);
  }
}
